// DS — KWOTA TOKENÓW JAKO DANA: glif monety + liczba, bez słowa (paczka P1 C, reguła 48).
//
// Właściciel 21.09 wieczór, dosłownie: „usun tokenow - niech bd token i liczba tylko
// (graficznie)". Geometria i lista miejsc: `design/claude-design-wyniki/32-minimal-muzyka-tekst/
// MAPA.md` § 32-R5c. To jest ten sam ruch, co reguła 8 na zdaniach objaśniających: fakt
// wraca jako DANA, a nie jako zdanie o danej — tylko że tu „zdaniem" było jedno słowo,
// powtórzone przy każdej liczbie na ekranie.
//
// JEDEN KOMPONENT NA CAŁY PRODUKT (wymaganie R5c). Żadne miejsce nie składa kwoty ręcznie:
// przed tą paczką składało ją dwadzieścia kilka miejsc po swojemu (`${n} ${tokenWord(...)}`),
// i dlatego usunięcie słowa byłoby dwudziestoma kilkoma poprawkami, z których jedna zawsze
// zostaje. Słowo ZOSTAJE w zdaniach, w tekstach prawnych, w nagłówku „Tokeny i plan",
// w tytule okna „Za mało tokenów", w akcji „Doładuj tokeny" i w NAZWACH wierszy danych
// („Zwrócone tokeny") — te miejsca nie wołają tego komponentu.
//
// CZYTNIK EKRANU DOSTAJE WIĘCEJ, NIE MNIEJ. Widoczny zapis jest `aria-hidden`, a obok stoi
// `sr-only` z pełną kwotą, odmienioną, ze znakiem wypowiedzianym słowem („plus 10 tokenów").
// Glif nigdy nie dopowiada: jest ozdobą liczby, nie drugą nazwą waluty.
//
// Rysunek monety podmieni koordynator w `ds/media/tokenCoin.ts` (dwie stałe) — ten plik
// wybiera tylko KTÓRY z dwóch krojów monety wziąć, bo poniżej 20 px otwory nut zamykają
// się w plamę (`token-s` ma własną geometrię na siatce 16).

import { Glyph } from '../media/Glyph'
import { groupCount } from '../../locales/format'
import { tokenWord } from '../../locales/plural'

/** Miejsca z tabeli R5c §B — rozmiar glifu i odstęp do liczby na każde z nich. */
export type TokenAmountPlace =
  | 'label' | 'table' | 'legend' | 'topbar' | 'nav' | 'pack' | 'plan' | 'balance'

/** Glif i odstęp glif–liczba (px), zmierzone na renderze arkusza `R5c-kwota-glif-liczba`. */
const PLACE: Record<TokenAmountPlace, { glyph: number; gap: number }> = {
  label: { glyph: 16, gap: 4 },
  table: { glyph: 16, gap: 4 },
  legend: { glyph: 14, gap: 4 },
  topbar: { glyph: 18, gap: 6 },
  nav: { glyph: 20, gap: 8 },
  pack: { glyph: 22, gap: 6 },
  plan: { glyph: 28, gap: 8 },
  balance: { glyph: 36, gap: 10 },
}

/** Znak stoi PRZED glifem i jest wymawiany słowem; minus to zwykły łącznik (reguła 7). */
const SIGN_WORD: Record<'en' | 'pl', Record<'+' | '-', string>> = {
  pl: { '+': 'plus', '-': 'minus' },
  en: { '+': 'plus', '-': 'minus' },
}

export interface TokenAmountProps {
  locale: 'en' | 'pl'
  /** Ile TOKENÓW — liczba z serwera, nigdy z pamięci ekranu. */
  n: number
  /** Gdzie ta kwota stoi (tabela R5c §B). Domyślnie etykieta płatnej akcji. */
  place?: TokenAmountPlace
  /** Zwrot / obciążenie: znak przed glifem, wypowiadany słowem. */
  sign?: '+' | '-'
  className?: string
  'data-testid'?: string
}

/**
 * Pełna kwota dla czytnika ekranu i dla propów, które przyjmują wyłącznie łańcuch
 * (`aria-label`, `title`) — tam glif nie ma jak stanąć, więc zostaje słowo.
 */
export function tokenAmountText(locale: 'en' | 'pl', n: number, sign?: '+' | '-'): string {
  const amount = `${groupCount(locale, n)} ${tokenWord(locale, n)}`
  return sign ? `${SIGN_WORD[locale][sign]} ${amount}` : amount
}

export function TokenAmount({ locale, n, place = 'label', sign, className = '', ...rest }: TokenAmountProps) {
  const { glyph, gap } = PLACE[place]
  return (
    <span
      className={['mn-tk', className].filter(Boolean).join(' ')}
      data-place={place}
      style={{ '--mn-tk-gap': `${gap}px` } as React.CSSProperties}
      data-testid={rest['data-testid']}
    >
      <span className="sr-only">{tokenAmountText(locale, n, sign)}</span>
      <span aria-hidden="true" className="mn-tk-face">
        {sign && <span className="mn-tk-sign">{sign}</span>}
        {/* Poniżej 20 px idzie mała moneta: siatka 24 skalowana do 16 zamyka otwory nut. */}
        <Glyph name={glyph < 20 ? 'token-s' : 'token'} size={glyph} tone="current" className="mn-tk-coin" />
        <span className="mn-tk-n">{groupCount(locale, n)}</span>
      </span>
    </span>
  )
}

export default TokenAmount
